import React, { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import axios from 'axios';
import { User, Lock, Mail, ArrowRight, Radio, CheckCircle, Eye, EyeOff } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthPage = () => {
  const location = useLocation();
  const fromState = location.state || {};
  const [isLogin, setIsLogin] = useState(!fromState.register);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [ageConfirmed, setAgeConfirmed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [verificationSent, setVerificationSent] = useState(false);
  const [verifyToken, setVerifyToken] = useState('');
  const { login, register, loginWithGoogle, user } = useAuth();
  const navigate = useNavigate();

  // Where to redirect after auth
  const redirectTo = fromState.from?.pathname || '/';
  const intent = fromState.intent || null;

  // Redirect if already logged in
  React.useEffect(() => {
    if (user) {
      navigate(redirectTo, { replace: true });
    }
  }, [user, navigate, redirectTo]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isLogin && !termsAccepted) {
      toast.error('Please accept the Terms of Service and Privacy Policy');
      return;
    }
    if (!isLogin && !ageConfirmed) {
      toast.error('You must confirm you are 18 years or older');
      return;
    }
    setLoading(true);

    try {
      if (isLogin) {
        await login(email, password);
        toast.success('Welcome back!');
        navigate(redirectTo, { replace: true, state: { intent } });
      } else {
        const result = await register(email, password, name);
        // Store terms acceptance
        try {
          const token = localStorage.getItem('auth_token');
          if (token) {
            await fetch(`${API}/auth/accept-terms`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
              body: JSON.stringify({ policy_version: '1.0', age_confirmed: true }),
            });
          }
        } catch (e) { console.error('Terms acceptance failed:', e); }
        toast.success('Account created! Please verify your email.');
        setVerificationSent(true);
        if (result?.verify_token) {
          setVerifyToken(result.verify_token);
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ backgroundColor: '#0A0A0B' }}>
      <div
        className="absolute inset-0 opacity-20"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1577915332909-39e7c140bf3c?crop=entropy&cs=srgb&fm=jpg&q=85)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          filter: 'blur(60px)'
        }}
      />

      <div className="relative z-10 w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2 mb-3 hover:opacity-80 transition-opacity">
            <Radio className="w-5 h-5 text-purple-500" />
            <h1
              className="text-4xl sm:text-5xl font-light tracking-tighter"
              style={{ fontFamily: "'Cormorant Garamond', serif", color: '#F8FAFC' }}
            >
              Opposite Direction
            </h1>
          </Link>
          <p className="text-sm" style={{ color: '#94A3B8' }}>
            AI-powered podcast generation
          </p>
        </div>

        {/* Auth Card */}
        <div className="glass-panel rounded-2xl p-8">
          {verificationSent ? (
            <div className="text-center" data-testid="verification-sent-banner">
              <CheckCircle className="w-12 h-12 text-emerald-400 mx-auto mb-4" />
              <h2 className="text-lg font-medium text-white mb-2">Check Your Email</h2>
              <p className="text-sm text-gray-400 mb-4">
                We've sent a verification link to <strong className="text-white">{email}</strong>.
              </p>
              {verifyToken && (
                <div className="rounded-lg p-4 mb-4" style={{ backgroundColor: 'rgba(109, 40, 217, 0.1)', border: '1px solid rgba(109, 40, 217, 0.2)' }}>
                  <p className="text-xs text-purple-300 mb-2">SMTP not configured. Use this link to verify:</p>
                  <Button size="sm" onClick={() => navigate(`/verify-email?token=${verifyToken}`)} className="bg-purple-600 hover:bg-purple-500 text-white text-xs" data-testid="verify-now-btn">
                    Verify Email Now
                  </Button>
                </div>
              )}
              <div className="flex gap-3 justify-center">
                <Button variant="outline" onClick={() => navigate('/')} className="border-white/10 text-white hover:bg-white/5" data-testid="skip-verification-btn">
                  Continue to Home
                </Button>
              </div>
            </div>
          ) : (
          <>
          {/* Tabs */}
          <div className="flex gap-4 mb-6">
            <button
              onClick={() => setIsLogin(true)}
              className={`flex-1 py-2 text-sm font-medium transition-colors ${
                isLogin
                  ? 'text-white border-b-2 border-purple-500'
                  : 'text-gray-500 border-b border-transparent hover:text-gray-300'
              }`}
              data-testid="login-tab"
            >
              Sign In
            </button>
            <button
              onClick={() => setIsLogin(false)}
              className={`flex-1 py-2 text-sm font-medium transition-colors ${
                !isLogin
                  ? 'text-white border-b-2 border-purple-500'
                  : 'text-gray-500 border-b border-transparent hover:text-gray-300'
              }`}
              data-testid="register-tab"
            >
              Create Account
            </button>
          </div>

          {/* Google Login */}
          <Button
            onClick={loginWithGoogle}
            variant="outline"
            className="w-full mb-6 h-12 border-white/10 bg-white/5 hover:bg-white/10 text-white"
            data-testid="google-login-btn"
          >
            <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
              <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Continue with Google
          </Button>

          <div className="relative mb-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-white/10"></div>
            </div>
            <div className="relative flex justify-center text-xs">
              <span className="px-2 text-gray-500" style={{ backgroundColor: 'rgba(10, 10, 11, 0.6)' }}>or</span>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {!isLogin && (
              <div>
                <Label htmlFor="name" className="text-gray-400 text-sm">Name</Label>
                <div className="relative mt-1">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                  <Input
                    id="name"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="pl-10 h-12 bg-white/5 border-white/10 text-white placeholder:text-gray-500 focus:border-purple-500 focus:ring-purple-500/20"
                    placeholder="Your name"
                    data-testid="name-input"
                  />
                </div>
              </div>
            )}

            <div>
              <Label htmlFor="email" className="text-gray-400 text-sm">Email</Label>
              <div className="relative mt-1">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10 h-12 bg-white/5 border-white/10 text-white placeholder:text-gray-500 focus:border-purple-500 focus:ring-purple-500/20"
                  placeholder="you@example.com"
                  required
                  data-testid="email-input"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="password" className="text-gray-400 text-sm">Password</Label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10 pr-10 h-12 bg-white/5 border-white/10 text-white placeholder:text-gray-500 focus:border-purple-500 focus:ring-purple-500/20"
                  placeholder="••••••••"
                  required
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300 transition-colors"
                  data-testid="toggle-password-btn"
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {/* Terms acceptance checkbox - register only */}
            {!isLogin && (
              <div className="space-y-2.5 pt-1">
                <div className="flex items-start gap-2.5">
                  <Checkbox
                    id="age"
                    checked={ageConfirmed}
                    onCheckedChange={setAgeConfirmed}
                    className="mt-0.5 border-white/20 data-[state=checked]:bg-purple-600 data-[state=checked]:border-purple-600"
                    data-testid="age-checkbox"
                  />
                  <label htmlFor="age" className="text-xs leading-relaxed cursor-pointer" style={{ color: '#94A3B8' }}>
                    I confirm that I am <strong className="text-white">18 years of age or older</strong>
                  </label>
                </div>
                <div className="flex items-start gap-2.5">
                  <Checkbox
                    id="terms"
                    checked={termsAccepted}
                    onCheckedChange={setTermsAccepted}
                    className="mt-0.5 border-white/20 data-[state=checked]:bg-purple-600 data-[state=checked]:border-purple-600"
                    data-testid="terms-checkbox"
                  />
                  <label htmlFor="terms" className="text-xs leading-relaxed cursor-pointer" style={{ color: '#94A3B8' }}>
                    I agree to the{' '}
                    <Link to="/terms" className="text-purple-400 hover:text-purple-300 underline" target="_blank">Terms of Service</Link>,{' '}
                    <Link to="/privacy" className="text-purple-400 hover:text-purple-300 underline" target="_blank">Privacy Policy</Link>, and{' '}
                    <Link to="/refund-policy" className="text-purple-400 hover:text-purple-300 underline" target="_blank">Refund Policy</Link>
                  </label>
                </div>
              </div>
            )}

            <Button
              type="submit"
              disabled={loading || (!isLogin && (!termsAccepted || !ageConfirmed))}
              className="w-full h-12 bg-purple-600 hover:bg-purple-500 text-white font-medium disabled:opacity-40"
              data-testid="auth-submit-btn"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  {isLogin ? 'Sign In' : 'Create Account'}
                  <ArrowRight className="ml-2 w-4 h-4" />
                </>
              )}
            </Button>

            {isLogin && (
              <div className="text-center pt-2">
                <Link to="/reset-password" className="text-xs text-purple-400 hover:text-purple-300 hover:underline" data-testid="forgot-password-link">
                  Forgot your password?
                </Link>
              </div>
            )}
          </form>
          </>
          )}
        </div>

        {/* Footer */}
        <p className="text-center text-xs mt-6" style={{ color: '#64748B' }}>
          By continuing, you agree to our{' '}
          <Link to="/terms" className="text-purple-400 hover:underline">Terms of Service</Link> and{' '}
          <Link to="/privacy" className="text-purple-400 hover:underline">Privacy Policy</Link>
        </p>
      </div>
    </div>
  );
};

export default AuthPage;
